import React, { useState, useMemo } from 'react';
import {
  TextField,
  Autocomplete,
  Chip,
  Box,
  Typography,
  FormControl,
  FormLabel,
  ListItem,
  ListItemText,
} from '@mui/material';

import { ImpactAiPrivileges, Privileges } from '../../../utils/privileges';

interface PrivilegeSelectorProps {
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
  helperText?: string;
}

// Map privileges to user-friendly descriptions
export const PRIVILEGE_DESCRIPTIONS: Record<string, string> = {
  [Privileges.FUNDRAISER_MANAGE]: 'Manage Campaigns - Create and edit fundraising campaigns',
  [Privileges.BASE_EVENT_MANAGE]: 'Manage Events - Create and edit standard events',
  [Privileges.EVENT_MANAGE]: 'Manage Volunteer Events - Create and edit volunteer opportunities',
  [Privileges.PROJECT_MANAGE]: 'Manage Projects - Create and edit volunteer projects',
};

const PRIVILEGES = Object.values(ImpactAiPrivileges);

const PrivilegeSelector: React.FC<PrivilegeSelectorProps> = ({
  value = [],
  onChange,
  label = 'Required Privileges',
  helperText = 'Select privileges required to access this preset.',
}) => {
  const [inputValue, setInputValue] = useState('');

  const options = useMemo(() => {
    const allOptions = new Set([...PRIVILEGES, ...value]);
    return Array.from(allOptions).sort();
  }, [value]);

  return (
    <FormControl fullWidth>
      <FormLabel component="legend" sx={{ mb: 1 }}>
        {label}
      </FormLabel>
      <Autocomplete
        multiple
        freeSolo
        options={options}
        value={value}
        onChange={(_, newValue) => {
          onChange(newValue);
        }}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);
        }}
        getOptionLabel={(option) => {
          const description = PRIVILEGE_DESCRIPTIONS[option];
          return description ? `${option} - ${description.split(' - ')[1]}` : option;
        }}
        renderOption={(props, option) => (
          <ListItem {...props} sx={{ display: 'block', py: 1 }}>
            <ListItemText
              primary={option}
              secondary={PRIVILEGE_DESCRIPTIONS[option]}
              primaryTypographyProps={{ variant: 'body2', fontFamily: 'monospace' }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </ListItem>
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const description = PRIVILEGE_DESCRIPTIONS[option];
            const chipLabel = description ? description.split(' - ')[0] : option;
            return (
              <Chip
                label={chipLabel}
                title={option}
                {...getTagProps({ index })}
                size="small"
                color="primary"
                variant="outlined"
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            placeholder="Select or type privileges"
            helperText={helperText}
          />
        )}
      />
      {value.length > 0 && (
        <Box mt={1}>
          <Typography variant="caption" color="text.secondary">
            {value.length} privilege{value.length !== 1 ? 's' : ''} selected
          </Typography>
        </Box>
      )}
    </FormControl>
  );
};

export default PrivilegeSelector;
